import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Sneaker, Target, ShoppingCart, ChartLineUp, Eye, Timer, Scales, Heart, BellRinging, Globe, Ruler, MagnifyingGlass, Check, Envelope } from '@phosphor-icons/react';
import axios from 'axios';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const brands = ['NIKE', 'ADIDAS', 'NEW BALANCE', 'ASICS', 'HOKA', 'ON', 'BROOKS', 'PUMA', 'SALOMON', 'REEBOK', 'CONVERSE', 'VANS', 'DR. MARTENS', 'UGG', 'TIMBERLAND', 'BLUNDSTONE', 'COLE HAAN', 'MERRELL', 'JORDAN', 'UNDER ARMOUR'];

const retailers = [
  { name: 'Nike', region: 'US' }, { name: 'Foot Locker', region: 'US' }, { name: 'Nordstrom', region: 'US' },
  { name: 'Zappos', region: 'US' }, { name: 'Amazon', region: 'US' }, { name: 'DSW', region: 'US' },
  { name: 'ASOS', region: 'UK' }, { name: 'JD Sports', region: 'UK' }, { name: 'Selfridges', region: 'UK' },
  { name: 'Harrods', region: 'UK' }, { name: 'Schuh', region: 'UK' }, { name: 'Zalando', region: 'UK' },
  { name: 'THE ICONIC', region: 'AU' }, { name: 'Myer', region: 'AU' }, { name: 'David Jones', region: 'AU' },
  { name: 'Platypus', region: 'AU' }, { name: 'rebel', region: 'AU' }, { name: "Athlete's Foot", region: 'AU' },
];
const flagEmoji = { AU: '\uD83C\uDDE6\uD83C\uDDFA', UK: '\uD83C\uDDEC\uD83C\uDDE7', US: '\uD83C\uDDFA\uD83C\uDDF8' };

const categories = [
  { name: 'Running', count: '240+', filter: 'sports' },
  { name: 'Basketball', count: '49', filter: 'basketball' },
  { name: 'Training', count: '55', filter: 'training', isTag: true },
  { name: 'Football', count: '67', filter: 'football' },
  { name: "Women's Fashion", count: '190+', filter: 'womens_fashion' },
  { name: "Men's Fashion", count: '42', filter: 'mens_fashion' },
  { name: 'Boots', count: '87', filter: 'boots' },
  { name: 'Outdoor', count: '63', filter: 'outdoor' },
  { name: 'School Shoes', count: '53', filter: 'school' },
  { name: 'Sandals', count: '62', filter: 'sandals' },
];

export default function HomePage() {
  const [popularShoes, setPopularShoes] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [stats, setStats] = useState({ shoes: 900, stores: 34, brands: 20 });
  const { region } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/shoes-popular`, { params: { region } }).then(({ data }) => setPopularShoes(data)).catch(() => {});
    axios.get(`${API}/shoes`, { params: { region } }).then(({ data }) => setStats(prev => ({ ...prev, shoes: data.length }))).catch(() => {});
  }, [region]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/email/subscribe`, { email, source: 'homepage' });
      setSubscribed(true);
    } catch { setSubscribed(true); }
  };

  const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 bg-[#FF3B30] px-4 py-1.5 mb-6">
                <Timer size={14} weight="bold" />
                <span className="text-xs font-bold uppercase tracking-[0.15em]">60-Second AI Analysis</span>
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter uppercase leading-[0.9]" data-testid="hero-title">
                FIND YOUR<br />
                PERFECT SHOE<br />
                <span className="text-[#FF3B30]">IN 60 SECONDS</span>
              </h1>
              <p className="mt-6 text-base md:text-lg font-medium leading-relaxed text-[#A1A1AA] max-w-xl" data-testid="hero-description">
                AI-powered shoe fitting that matches you with the right shoe from {stats.shoes}+ products across {stats.stores} retailers in Australia, the UK, and the US.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/quiz" className="inline-flex items-center gap-3 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors text-base" data-testid="hero-cta-quiz">
                  Find Your Perfect Fit <ArrowRight size={20} weight="bold" />
                </Link>
                <Link to="/browse" className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-white/30 font-bold uppercase tracking-wide px-8 py-4 hover:border-white hover:bg-white/5 transition-colors" data-testid="hero-cta-browse">
                  Browse All Shoes
                </Link>
              </div>
              {/* Trust stats */}
              <div className="mt-10 flex gap-8 flex-wrap">
                <div><span className="font-black text-2xl text-[#FF3B30]">{stats.shoes}+</span><p className="text-xs text-[#A1A1AA] font-bold uppercase mt-1">Shoes</p></div>
                <div><span className="font-black text-2xl text-[#FF3B30]">{stats.stores}</span><p className="text-xs text-[#A1A1AA] font-bold uppercase mt-1">Retailers</p></div>
                <div><span className="font-black text-2xl text-[#FF3B30]">3</span><p className="text-xs text-[#A1A1AA] font-bold uppercase mt-1">Regions</p></div>
                <div><span className="font-black text-2xl text-[#FF3B30]">10</span><p className="text-xs text-[#A1A1AA] font-bold uppercase mt-1">Categories</p></div>
              </div>
            </motion.div>
            <motion.div className="relative hidden lg:block" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#FF3B30]/10 -rotate-2" />
                <img src="https://images.unsplash.com/photo-1710643301117-4d738aeb1e69?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200" alt="Premium sneakers close up" className="relative w-full aspect-[4/3] object-cover" data-testid="hero-image" />
                <div className="absolute bottom-4 left-4 right-4 bg-[#0A0A0A]/90 backdrop-blur-sm p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase font-bold">Comparing across</p>
                    <p className="text-sm font-bold">AU &middot; UK &middot; US retailers</p>
                  </div>
                  <div className="flex gap-2 text-lg">{'\uD83C\uDDE6\uD83C\uDDFA \uD83C\uDDEC\uD83C\uDDE7 \uD83C\uDDFA\uD83C\uDDF8'}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Marquee */}
      <section className="py-4 border-b border-[#E4E4E7] bg-white overflow-hidden">
        <Marquee speed={35} gradient={false}>
          {brands.map((brand, i) => (
            <span key={i} className="mx-6 font-heading font-black text-2xl tracking-tighter uppercase text-[#E4E4E7] hover:text-[#0A0A0A] transition-colors cursor-default">{brand}</span>
          ))}
        </Marquee>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div className="text-center mb-14" {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-3">How It Works</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase">Three Steps to Your Perfect Shoe</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: <Ruler size={36} weight="duotone" className="text-[#FF3B30]" />, title: 'TAKE THE QUIZ', desc: 'Answer 8 quick questions about your foot size, width, activity, and style preferences. Our AI measures your perfect fit.' },
              { step: '02', icon: <MagnifyingGlass size={36} weight="duotone" className="text-[#FF3B30]" />, title: 'GET MATCHED', desc: "We scan 900+ shoes across 34 retailers in AU, UK, and US to find your best matches ranked by fit score." },
              { step: '03', icon: <ShoppingCart size={36} weight="duotone" className="text-[#FF3B30]" />, title: 'COMPARE & SHOP', desc: 'Compare prices across stores, save to your wishlist, and shop from the retailer with the best deal.' },
            ].map((item, i) => (
              <motion.div key={i} className="relative border-2 border-[#E4E4E7] p-8 hover:border-[#0A0A0A] transition-colors group"
                {...fadeUp} transition={{ delay: i * 0.15 }}>
                <span className="absolute -top-5 left-6 bg-white px-3 font-heading font-black text-4xl text-[#E4E4E7] group-hover:text-[#FF3B30] transition-colors">{item.step}</span>
                <div className="mt-4 mb-4">{item.icon}</div>
                <h3 className="font-heading font-bold text-lg tracking-tight mb-3">{item.title}</h3>
                <p className="text-sm text-[#52525B] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/quiz" className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-10 py-4 hover:bg-[#D62828] transition-colors" data-testid="how-it-works-cta">
              Start Your Free Analysis <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div className="text-center mb-14" {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-3">Why SoleFinder</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase">Everything You Need to Find the Perfect Shoe</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Target size={28} weight="duotone" />, title: 'AI Fit Analysis', desc: 'Foot length, width, arch type — our quiz builds your fit profile in 60 seconds.' },
              { icon: <Scales size={28} weight="duotone" />, title: 'Side-by-Side Compare', desc: 'Compare 2-4 shoes with region pricing, features, and fit data in one view.' },
              { icon: <Globe size={28} weight="duotone" />, title: '3-Region Coverage', desc: 'Shop across Australia, UK, and US retailers. Prices in your local currency.' },
              { icon: <BellRinging size={28} weight="duotone" />, title: 'Price Drop Alerts', desc: 'Real-time notifications when wishlisted shoes drop in price. Never miss a deal.' },
              { icon: <Heart size={28} weight="duotone" />, title: 'Smart Wishlist', desc: 'Save favourites, track prices, compare from your wishlist. One-click to buy.' },
              { icon: <Ruler size={28} weight="duotone" />, title: 'Foot Measurement', desc: 'Enter your foot length and width in cm. We recommend your size and width fitting.' },
            ].map((item, i) => (
              <motion.div key={i} className="border border-white/10 p-6 hover:border-[#FF3B30]/50 transition-colors"
                {...fadeUp} transition={{ delay: i * 0.1 }}>
                <div className="text-[#FF3B30] mb-3">{item.icon}</div>
                <h3 className="font-bold text-base uppercase tracking-wide mb-2">{item.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 md:py-24 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-3">Shop by Category</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase">Every Type of Shoe, Covered</h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {categories.map((cat, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}>
                <Link to={`/browse?${cat.isTag ? 'tag' : 'category'}=${cat.filter}`} className="block border-2 border-[#E4E4E7] bg-white p-5 text-center hover:border-[#FF3B30] transition-colors group">
                  <p className="font-heading font-bold text-sm uppercase tracking-tight group-hover:text-[#FF3B30] transition-colors">{cat.name}</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">{cat.count} shoes</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR PICKS */}
      {popularShoes.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">Trending Now</p>
                <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0A0A0A] uppercase" data-testid="popular-picks-title">Popular Picks</h2>
              </div>
              <Link to="/browse" className="text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors flex items-center gap-1">
                View All <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="popular-picks-grid">
              {popularShoes.map((shoe, i) => (
                <motion.div key={shoe.shoe_id} {...fadeUp} transition={{ delay: i * 0.1 }}>
                  <Link to={`/shoe/${shoe.shoe_id}`} className="block border border-[#E4E4E7] bg-white hover:border-[#0A0A0A] transition-all group" data-testid={`popular-pick-${shoe.shoe_id}`}>
                    <div className="overflow-hidden relative">
                      <img src={shoe.image_url} alt={`${shoe.brand} ${shoe.model}`} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-4">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{shoe.brand}</p>
                      <p className="font-heading font-bold text-sm mt-1">{shoe.model}</p>
                      <p className="text-xs text-[#52525B] capitalize mt-1">{shoe.category?.replace('_', ' ')}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RETAILERS */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-3">Our Retail Partners</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase">34 Retailers Across 3 Regions</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {['US', 'UK', 'AU'].map(region => (
              <div key={region}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">{flagEmoji[region]}</span>
                  <h3 className="font-bold text-lg uppercase">{region === 'AU' ? 'Australia' : region === 'UK' ? 'United Kingdom' : 'United States'}</h3>
                </div>
                <div className="space-y-2">
                  {retailers.filter(r => r.region === region).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                      <Check size={14} className="text-[#FF3B30]" />
                      {r.name}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stores" className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-bold uppercase tracking-wide px-8 py-3 hover:border-white hover:bg-white/5 transition-colors text-sm">
              View Store Directory <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* EMAIL SIGNUP */}
      <section className="py-16 md:py-24 bg-[#FF3B30]" data-testid="email-signup-section">
        <div className="max-w-[700px] mx-auto px-6 md:px-12 text-center text-white">
          <motion.div {...fadeUp}>
            <Envelope size={40} className="mx-auto mb-4 opacity-80" />
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase mb-4">Never Miss a Price Drop</h2>
            <p className="text-base opacity-90 mb-8">Subscribe to get alerts when shoes you love drop in price. Free, no spam, unsubscribe anytime.</p>
            {subscribed ? (
              <div className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4">
                <Check size={20} /> Subscribed! Check your inbox.
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-0 max-w-md mx-auto">
                <input type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-5 py-4 bg-white text-[#0A0A0A] font-medium outline-none text-sm" data-testid="email-signup-input" required />
                <button type="submit" className="bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-6 py-4 hover:bg-[#333] transition-colors text-sm" data-testid="email-signup-submit">
                  Subscribe
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-24 bg-white text-center">
        <div className="max-w-[700px] mx-auto px-6">
          <motion.div {...fadeUp}>
            <Sneaker size={48} weight="duotone" className="text-[#FF3B30] mx-auto mb-4" />
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase mb-4">Ready to Find<br /><span className="text-[#FF3B30]">Your Perfect Shoe?</span></h2>
            <p className="text-[#52525B] text-base mb-8 max-w-lg mx-auto">Join thousands of shoppers who found their perfect fit. It only takes 60 seconds.</p>
            <Link to="/quiz" className="inline-flex items-center gap-3 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-10 py-5 hover:bg-[#D62828] transition-colors text-base" data-testid="final-cta">
              Find Your Perfect Fit <ArrowRight size={22} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A0A0A] text-white py-12 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sneaker size={24} weight="duotone" className="text-[#FF3B30]" />
                <span className="font-heading font-black text-lg tracking-tighter uppercase">SOLEFINDER</span>
              </div>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">AI-powered shoe discovery across Australia, United Kingdom, and United States.</p>
            </div>
            <div>
              <h4 className="font-bold text-xs uppercase tracking-wide mb-3">Discover</h4>
              <div className="flex flex-col gap-2 text-sm text-[#A1A1AA]">
                <Link to="/quiz" className="hover:text-white transition-colors">Find Your Fit</Link>
                <Link to="/browse" className="hover:text-white transition-colors">Browse Shoes</Link>
                <Link to="/compare" className="hover:text-white transition-colors">Compare</Link>
                <Link to="/stores" className="hover:text-white transition-colors">Store Directory</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-xs uppercase tracking-wide mb-3">Account</h4>
              <div className="flex flex-col gap-2 text-sm text-[#A1A1AA]">
                <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
                <Link to="/register" className="hover:text-white transition-colors">Create Account</Link>
                <Link to="/wishlist" className="hover:text-white transition-colors">My Wishlist</Link>
                <Link to="/price-drops" className="hover:text-white transition-colors">Price Drops</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-xs uppercase tracking-wide mb-3">Legal</h4>
              <div className="flex flex-col gap-2 text-sm text-[#A1A1AA]">
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
                <Link to="/terms#affiliate" className="hover:text-white transition-colors">Affiliate Disclosure</Link>
                <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact Us</Link>
                <span className="cursor-default text-xs">hello@solefinder.app</span>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col gap-4">
            <div className="bg-white/5 border-l-2 border-[#FF3B30] px-4 py-3 text-xs text-[#D4D4D8] leading-relaxed" data-testid="affiliate-disclosure">
              <strong className="font-bold text-white uppercase tracking-wide mr-2">Affiliate disclosure:</strong>
              SoleFinder is a content-driven comparison platform. We participate in affiliate programmes including Amazon Associates, CJ Affiliate, Awin, Commission Factory, Sovrn Commerce, and Impact. When you click an outbound retailer link, we may earn a commission at no extra cost to you. Editorial recommendations are based on our AI fit quiz and product attributes — not on commission rates. <a href="/terms#affiliate" className="underline hover:text-white">Read full disclosure</a>.{" "}
              <a href="https://sovrn.co/bjk0r08" rel="nofollow sponsored noopener noreferrer" className="opacity-50 hover:opacity-100">Partner</a>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-2">
              <p className="text-xs text-[#A1A1AA]">&copy; 2026 SoleFinder. All rights reserved. solefinder.app</p>
              <p className="text-xs text-[#52525B]">AI-powered shoe discovery · AU · UK · US</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
